import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { doc, getDoc, setDoc } from "./firebase-shim";
import { apriPimus, AZIENDA_DEFAULT, SQUADRA_DEFAULT, dataIt } from "./PonteggioPimus";

// PONT_PIMUS_v1 PONT_PIMUS_v2 - dati per il PIMUS.
// Cantiere: si salvano con il ponteggio. Impresa: impostazioni/pimus_azienda.
// Squadra: dalla sezione Lavoratori (/api/lavoratori), chi e' attivo e ha la formazione ponteggi (art. 136).

const inp = { width: "100%", boxSizing: "border-box", padding: "4px 6px", borderRadius: 5, border: "1px solid rgba(128,128,128,0.4)", background: "rgba(128,128,128,0.08)", color: "inherit", fontSize: 12 };
const lab = { fontSize: 11, opacity: 0.75, display: "block" };
const CAMPI = [
  ["lavori", "Lavori", "es. tinteggiatura facciate e manutenzione gronde"],
  ["comune", "Comune", "es. Serravalle Pistoiese (PT)"],
  ["committente", "Committente (se diverso dal cliente)", ""],
  ["dataMontaggio", "Montaggio previsto", "gg/mm/aaaa"],
  ["durata", "Durata prevista", "es. 6 settimane"],
  ["dl", "Direttore dei lavori", "non nominato"],
  ["cse", "Coordinatore esecuzione (CSE)", "non nominato"],
  ["stoccaggio", "Zona di stoccaggio del materiale", "es. cortile lato nord"],
  ["accesso", "Accesso mezzi e zona di carico", "es. da via Dori, camion in strada con movieri"],
  ["appoggio", "Ripartizione del carico", "tavole di legno sotto ogni basetta"],
  ["revisione", "Revisione", "00"],
];

const pieno = o => !!o && typeof o === "object" && Object.keys(o).length > 0;
// formazione ponteggi di un lavoratore: testo per il PIMUS e scadenza (la piu' lontana tra corso e aggiornamento)
function formazionePonteggi(f) {
  const p = (f && f.ponteggi_pimus) || {}, a = (f && f.aggiornamento_ponteggi) || {};
  if (!pieno(p) && !pieno(a)) return null;
  const parti = [];
  if (pieno(p)) { const d = p.data_fine || p.data; parti.push((p.tipo || "Corso ponteggi art. 136") + (d ? " del " + d : p.note ? " (" + String(p.note).toLowerCase() + ")" : "")); }
  if (pieno(a)) parti.push("aggiornamento" + (a.ore ? " " + a.ore + " h" : "") + (a.data ? " del " + a.data : ""));
  const sc = [p.scadenza, a.scadenza].map(dataIt).filter(Boolean).sort((x, y) => y - x)[0];
  return { attestato: parti.join(" · "), scadenza: sc ? sc.toLocaleDateString("it-IT") : "" };
}
// PONT_ATTPDF_v1: attestati PDF caricati nella scheda del lavoratore (/api/lavoratori/<id>/files): uso quelli con "ponteg" nel nome
function dataQ(t) {
  const s = String(t || "");
  let m = s.match(/(\d{4})-(\d{1,2})-(\d{1,2})/); if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/); return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null;
}
async function pdfPonteggi(id) {
  try {
    const r = await fetch("/api/lavoratori/" + id + "/files", { headers: authHeaders() });
    const j = await r.json();
    if (!r.ok || !j.ok) return null;
    const tutti = [].concat(...Object.values(j.files || {}).map(v => Array.isArray(v) ? v : []));
    let best = null;
    tutti.forEach(it => {
      if (!it || typeof it !== "object") return;
      const nome = String(it.nome || it.name || it.filename || it.file || it.originalName || it.titolo || "");
      const d = dataQ(it.scadenza);
      if (!/ponteg/i.test(nome) || !d) return;
      if (!best || d > best.d) best = { d, nome: nome.replace(/\.pdf$/i, "") };
    });
    return best ? { scadenza: best.d.toLocaleDateString("it-IT"), nome: best.nome, d: best.d } : null;
  } catch (e) { return null; }
}
function unisciPdf(fp, p) {
  if (!p) return fp;
  if (!fp) return { attestato: p.nome + " (PDF)", scadenza: p.scadenza };
  const d0 = dataIt(fp.scadenza);
  if (d0 && d0 >= p.d) return fp;
  return { ...fp, attestato: fp.attestato + " · scadenza dal PDF «" + p.nome + "»", scadenza: p.scadenza };
}
function authHeaders() {
  const t = typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem("bertoc_jwt") : null;
  return t ? { Authorization: "Bearer " + t } : {};
}

export default function PimusDati({ cantiere, descrizione, ris, puntiVal, calcolo, pimus = {}, onPimus }) {
  const [azienda, setAzienda] = useState(null);
  const [abilitati, setAbilitati] = useState(null);   // lavoratori con formazione ponteggi
  const [apri, setApri] = useState(false);
  const [modAz, setModAz] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const s = await getDoc(doc(db, "impostazioni", "pimus_azienda"));
        const ex = s && (typeof s.exists === "function" ? s.exists() : !!s.exists);
        const d = ex ? s.data() : null;
        setAzienda({ ...AZIENDA_DEFAULT, ...((d && d.azienda) || {}) });
      } catch (e) { setAzienda(AZIENDA_DEFAULT); setMsg("Dati impresa non caricati: " + e.message); }
      try {
        const r = await fetch("/api/lavoratori", { headers: authHeaders() });
        const j = await r.json();
        if (!r.ok || !j.ok) throw new Error(j.error || "HTTP " + r.status);
        const attivi = (j.lavoratori || []).filter(l => l.attivo !== false && l.attivo !== 0 && !l.cestino);
        const pdf = await Promise.all(attivi.map(l => pdfPonteggi(l.id))); // PONT_ATTPDF_v1: scadenza anche dai PDF degli attestati
        const lista = attivi
          .map((l, i) => ({ l, fp: unisciPdf(formazionePonteggi(l.formazione), pdf[i]) }))
          .filter(x => x.fp)
          .map(({ l, fp }) => ({ id: l.id, nome: l.nome || "", cognome: l.cognome || "", ...fp }))
          .sort((a, b) => (a.cognome + a.nome).localeCompare(b.cognome + b.nome));
        setAbilitati(lista);
      } catch (e) { setAbilitati([]); setMsg("Lavoratori non caricati (" + e.message + "): nel PIMUS uso l'elenco di riserva."); }
    })();
  }, []);

  const salvaAz = async () => {
    try { await setDoc(doc(db, "impostazioni", "pimus_azienda"), { azienda, updatedAt: new Date().toISOString() }); setModAz(false); setMsg("Dati impresa salvati."); }
    catch (e) { setMsg("Salvataggio non riuscito: " + e.message); }
  };
  const cambiaAz = (k, v) => { setAzienda(a => ({ ...a, [k]: v })); setModAz(true); };

  const esclusi = pimus.esclusi || [];
  const lista = abilitati || [];
  const marco = lista.find(x => /marco/i.test(x.nome) && /bertocci/i.test(x.cognome));
  const prepostoId = pimus.preposto || (marco ? marco.id : lista[0] && lista[0].id);
  const inSquadra = lista.filter(x => !esclusi.includes(x.id));
  const squadra = inSquadra.length
    ? [...inSquadra].sort((a, b) => (b.id === prepostoId) - (a.id === prepostoId)).map(x => ({ ...x, ruolo: x.id === prepostoId ? "Preposto" : "Lavoratore" }))
    : SQUADRA_DEFAULT;
  const oggi = new Date();
  const scaduti = inSquadra.filter(x => { const d = dataIt(x.scadenza); return d && d < oggi; });
  const senzaData = inSquadra.filter(x => !x.scadenza);
  const toggle = id => onPimus && onPimus({ ...pimus, esclusi: esclusi.includes(id) ? esclusi.filter(e => e !== id) : [...esclusi, id] });
  const genera = () => apriPimus({ cantiere, descrizione, ris, puntiVal, calcolo, pimus, azienda: azienda || AZIENDA_DEFAULT, squadra });

  return (
    <div style={{ marginTop: 14, padding: 10, border: "1px solid rgba(128,128,128,0.35)", borderRadius: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <b style={{ fontSize: 13 }}>📄 PIMUS</b>
        <button className="btn btn-primary btn-sm" onClick={genera} disabled={!ris || abilitati === null}>Genera bozza PIMUS</button>
        <span style={{ fontSize: 11, opacity: 0.7 }}>Usa il progetto calcolato qui sopra. I campi vuoti escono evidenziati in giallo nel documento.</span>
      </div>

      <div style={{ fontWeight: 600, fontSize: 12, margin: "10px 0 4px" }}>Squadra di montaggio (dalla sezione Lavoratori, con formazione ponteggi)</div>
      {abilitati === null && <div style={{ fontSize: 12, opacity: 0.7 }}>Carico i lavoratori...</div>}
      {abilitati && abilitati.length === 0 && <div style={{ fontSize: 12, color: "#d97706" }}>Nessun lavoratore attivo con formazione ponteggi trovato.</div>}
      {lista.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead><tr>{["In squadra", "Preposto", "Lavoratore", "Formazione ponteggi", "Valida fino al"].map(h => <th key={h} style={{ textAlign: "left", fontSize: 11, opacity: 0.7, padding: 4 }}>{h}</th>)}</tr></thead>
          <tbody>
            {lista.map(x => { const d = dataIt(x.scadenza); const scad = d && d < oggi; const dentro = !esclusi.includes(x.id); return (
              <tr key={x.id} style={{ opacity: dentro ? 1 : 0.45 }}>
                <td style={{ padding: 3 }}><input type="checkbox" checked={dentro} onChange={() => toggle(x.id)} /></td>
                <td style={{ padding: 3 }}><input type="radio" name="preposto_pimus" checked={x.id === prepostoId} disabled={!dentro} onChange={() => onPimus && onPimus({ ...pimus, preposto: x.id })} /></td>
                <td style={{ padding: 3, fontWeight: 600 }}>{x.cognome} {x.nome}</td>
                <td style={{ padding: 3 }}>{x.attestato}</td>
                <td style={{ padding: 3, color: scad ? "#dc2626" : !x.scadenza ? "#d97706" : undefined, fontWeight: scad ? 700 : 400 }}>{x.scadenza ? x.scadenza + (scad ? " — scaduto" : "") : "data non registrata"}</td>
              </tr>); })}
          </tbody>
        </table>
      )}
      {(scaduti.length > 0 || senzaData.length > 0) && (
        <div style={{ fontSize: 12, color: "#d97706", marginTop: 6 }}>
          {scaduti.length > 0 && <>⚠ Formazione ponteggi scaduta: {scaduti.map(x => x.nome + " " + x.cognome).join(", ")}. </>}
          {senzaData.length > 0 && <>Inserisci le date in Sicurezza → Lavoratori per: {senzaData.map(x => x.nome + " " + x.cognome).join(", ")}.</>}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 8, marginTop: 10 }}>
        {CAMPI.map(([k, l, ph]) => (
          <label key={k}><span style={lab}>{l}</span><input style={inp} value={pimus[k] || ""} placeholder={ph} onChange={e => onPimus && onPimus({ ...pimus, [k]: e.target.value })} /></label>
        ))}
      </div>
      <label style={{ display: "block", marginTop: 8 }}><span style={lab}>Note per il PIMUS</span><textarea style={{ ...inp, minHeight: 40 }} value={pimus.note || ""} onChange={e => onPimus && onPimus({ ...pimus, note: e.target.value })} /></label>
      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>Squadra, preposto e dati del cantiere si salvano con il pulsante Salva in alto.</div>

      <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={() => setApri(!apri)}>{apri ? "▾" : "▸"} Dati dell'impresa (valgono per tutti i cantieri)</button>
      {apri && azienda && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 8 }}>
            {[["ragione", "Ragione sociale"], ["piva", "P.IVA"], ["indirizzo", "Indirizzo sede"], ["citta", "CAP e città"], ["tel", "Telefono"], ["email", "E-mail"], ["legale", "Datore di lavoro / legale rappr."]].map(([k, l]) => (
              <label key={k}><span style={lab}>{l}</span><input style={inp} value={azienda[k] || ""} onChange={e => cambiaAz(k, e.target.value)} /></label>
            ))}
          </div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={salvaAz} disabled={!modAz}>{modAz ? "💾 Salva dati impresa" : "✓ Salvato"}</button>
        </div>
      )}
      {msg && <div style={{ fontSize: 11, opacity: 0.8, marginTop: 6 }}>{msg}</div>}
    </div>
  );
}
